import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Users, UserPlus, Loader2, KeyRound, Trash2, Copy, Check, Mail, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole } from "@/lib/roles";

const MAX_USERS = 3;

export default function OrgClienteUsers({ orgId, orgName }) {
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, users: [], max_users: MAX_USERS });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [creating, setCreating] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {email, temporary_password}
  const [copied, setCopied] = useState(false);
  const isAdmin = hasRole(user, "admin");

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/organizations/${orgId}/users`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  }, [orgId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  function genTempPwd() {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghjkmnpqrstuvwxyz";
    const digits = "23456789";
    const sym = "!@#$%&*";
    const all = upper + lower + digits + sym;
    let pwd = upper[Math.floor(Math.random() * upper.length)]
      + lower[Math.floor(Math.random() * lower.length)]
      + digits[Math.floor(Math.random() * digits.length)]
      + sym[Math.floor(Math.random() * sym.length)];
    for (let i = 0; i < 8; i++) pwd += all[Math.floor(Math.random() * all.length)];
    return pwd.split("").sort(() => Math.random() - 0.5).join("");
  }

  async function createUser(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Nome, cognome ed email obbligatori");
      return;
    }
    setCreating(true);
    try {
      const tempPwd = genTempPwd();
      const r = await api.post("/users", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: tempPwd,
        roles: ["cliente"],
        organization_id: orgId,
      });
      // Mostra credenziali generate + esito invio email
      setResetResult({
        email: r.data.email,
        temporary_password: tempPwd,
        email_sent: r.data.email_sent,
      });
      setShowAdd(false);
      setForm({ first_name: "", last_name: "", email: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore creazione");
    } finally { setCreating(false); }
  }

  async function resetPassword(u) {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetResult(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  async function deleteUser(u) {
    try {
      await api.post(`/users/${u.id}/deactivate`);
      toast.success(`Utente ${u.email} disattivato`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  function copyPwd() {
    if (!resetResult) return;
    navigator.clipboard.writeText(resetResult.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  if (!isAdmin) return null;
  if (loading) {
    return <div className="p-4 flex items-center gap-2 text-xs text-slate-400">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento utenti…
    </div>;
  }

  const remaining = MAX_USERS - data.count;
  const full = remaining <= 0;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`org-users-panel-${orgId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Users className="w-3.5 h-3.5" /> Utenti cliente
          <span className={`ml-1 normal-case font-bold ${full ? "text-amber-700" : "text-viva-700"}`}>
            {data.count}/{MAX_USERS}
          </span>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              disabled={full}
              data-testid={`add-org-user-${orgId}`}
              className="h-7 px-2 text-viva-700 hover:bg-viva-50 disabled:opacity-40"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Aggiungi
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid={`add-user-dialog-${orgId}`}>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Nuovo utente cliente</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Per <b>{orgName}</b>. Verrà generata una password temporanea da consegnare al cliente.</p>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nome *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-user-name" className="mt-1 h-10 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cognome *</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-user-last-name" className="mt-1 h-10 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="mt-1 h-10 rounded-lg" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Annulla</Button>
                <Button type="submit" disabled={creating} data-testid="new-user-submit" className="bg-viva-500 hover:bg-viva-600 text-white">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Crea utente
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {data.count === 0 ? (
        <div className="text-xs text-slate-400 py-2 italic" data-testid={`org-users-empty-${orgId}`}>Nessun utente cliente assegnato.</div>
      ) : (
        <ul className="space-y-1.5" data-testid={`org-users-list-${orgId}`}>
          {data.users.map((u) => (
            <li
              key={u.id}
              data-testid={`org-user-row-${u.id}`}
              className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 text-sm"
            >
              <div className="w-7 h-7 rounded-full bg-viva-100 text-viva-700 flex items-center justify-center text-xs font-bold shrink-0">
                {(u.name || u.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                  {u.name || "—"}
                  {u.must_change_password && (
                    <span title="Deve cambiare password al prossimo login" className="text-amber-600">
                      <ShieldAlert className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {u.email}
                </div>
              </div>
              <Button
                size="sm" variant="ghost" onClick={() => resetPassword(u)}
                data-testid={`reset-pwd-${u.id}`}
                title="Reset password"
                className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50"
              >
                <KeyRound className="w-3.5 h-3.5" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm" variant="ghost"
                    data-testid={`del-user-${u.id}`}
                    title="Disattiva utente"
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disattivare l&apos;utente?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Stai per disattivare l&apos;utente <b>{u.email}</b>. L&apos;accesso verrà revocato immediatamente; l&apos;account e il suo storico restano conservati.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`del-user-confirm-${u.id}`}
                      onClick={() => deleteUser(u)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Disattiva
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </li>
          ))}
        </ul>
      )}

      {/* Temporary password reveal */}
      <Dialog open={Boolean(resetResult)} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="max-w-md" data-testid="temp-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" /> Password temporanea generata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {resetResult?.email_sent
                ? <>Le credenziali sono state <b className="text-viva-700">inviate via email</b> a <b>{resetResult?.email}</b>. Puoi anche comunicarle manualmente dal box sottostante.</>
                : <>Comunica queste credenziali al cliente. Al primo login dovrà <b>cambiare la password</b>.</>}
            </p>
            <Card className="p-4 rounded-xl bg-slate-50 border-slate-200 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Email</div>
              <div className="font-mono text-sm text-slate-900">{resetResult?.email}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold pt-2 border-t border-slate-200">Password temporanea</div>
              <div className="flex items-center gap-2">
                <code data-testid="temp-pwd-value" className="font-mono text-sm bg-white px-3 py-2 rounded-lg border border-slate-200 flex-1 break-all">
                  {resetResult?.temporary_password}
                </code>
                <Button
                  size="sm" type="button" onClick={copyPwd} data-testid="temp-pwd-copy"
                  className="bg-viva-500 hover:bg-viva-600 text-white shrink-0"
                >
                  {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                  {copied ? "Copiato" : "Copia"}
                </Button>
              </div>
            </Card>
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              ⚠ Questa password sarà mostrata una sola volta. Salvala o copiala adesso.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} data-testid="temp-pwd-close">Ho copiato, chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
